import React from 'react';
import styled from '@emotion/styled';
import { batch, useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  fetchSubscriptionPackage,
  setIsUpdatedUpgradeModalShowing,
  setIsUpgradeModalShowing,
  setSelectedPackage,
  setIsInstantPaymentModalShowing,
} from '../../ducks/sportsScienceActions';
import { subscriptionFlowTracker, dashboardTracker } from '../../../../shared/utils/amplitudeHelper';

const CardWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  align-items: flex-start;
  justify-content: center;
  width: 344px;
  min-width: 260px;
  height: ${(props) => (props.hasButton ? '350px' : '300px')};
  cursor: pointer;
  box-shadow: 0px 1px 8px 0px rgba(8, 5, 10, 0.1);
  position: relative;
  background-color: #ffffff;

  ::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
    padding: 4px;
    background: ${(props) => props.gradient || 'none'};
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: destination-out;
    transition: background 0.3s ease;
  }

  &:hover::after {
    background: rgba(0, 0, 255, 0.2);
  }
`;

const CardImage = styled('div')`
  display: flex;
  border-top-right-radius: 12px;
  border-top-left-radius: 12px;
  width: 100%;
  background: url(${(props) => props.bgImage}) lightgray 50% / cover no-repeat;
  background-position: center;
  height: 200px;
  opacity: 0.8;
`;

const InnerContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  background: white;
  width: 100%;
  padding: 20px;
  gap: 12px;
  border-bottom-right-radius: 12px;
  border-bottom-left-radius: 12px;
  height: ${(props) => (props.hasButton ? '175px' : '125px')};
  max-height: ${(props) => (props.hasButton ? '175px' : '125px')};
`;

const CardText = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  font-family: 'Nunito Sans';
  font-feature-settings: 'liga' off, 'clig' off;
  background: white;
  width: 100%;
  gap: 12px;
`;

const CardHeaderContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const CardHeader = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 10000px;
  padding: 8px 12px;
  gap: 8px;
  background: #eeeeee;
  font-family: 'Nunito Sans';
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 16px;
  font-style: normal;
  font-weight: 800;
  line-height: 24px;
  letter-spacing: -0.32px;
`;

const ModuleIcon = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModuleTitle = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans';
  font-feature-settings: 'liga' off, 'clig' off;
  font-weight: 700;
  font-size: 14px;
  line-height: 18px;
`;

const ModuleSubtitle = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  color: '#9e9e9e';
  font-family: 'Nunito Sans';
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 11px;
  line-height: 14px;
`;

const CardDescription = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  font-family: 'Nunito Sans';
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  height: 40px;
`;

const PurchaseButton = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  padding: 12px 20px;
  width: 100%;
  border-radius: 10000px;
  border: none;
  box-shadow: 0px 2px 9px 0px rgba(0, 0, 0, 0.12);
  font-family: 'Nunito Sans';
  font-feature-settings: 'liga' off, 'clig' off;
  font-size: 12px;
  font-style: normal;
  font-weight: 900;
  line-height: normal;
  letter-spacing: 0.96px;
  text-transform: uppercase;
  cursor: pointer;
  color: #ffffff;
  background: linear-gradient(270deg, #6a82fb 0%, #fc5c7d 100%);
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  &:hover {
    background: linear-gradient(270deg, #4a62db 0%, #dc3c5d 100%);
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.2);
  }
`;

const LockOverlay = styled('div')`
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(100% - 8px);
  height: calc(100% - 8px);
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const LockIconCircle = styled('div')`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${(props) => props.backgroundColor || '#FFA500'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const ModuleCard = ({
  cardData, purchaseButton, locked,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const {
    addOnId,
    backgroundImage,
    icon,
    title,
    legacyTitle,
    description,
    activeTab,
    gradient,
  } = cardData;

  const handleUnlockedCardClick = () => {
    dashboardTracker(legacyTitle || title, null, null);
    dispatch(push(activeTab));
  };

  const handleLockedCardClick = () => {
    subscriptionFlowTracker(
      'Modal Viewed',
      legacyTitle || title,
      'AMS Homepage',
    );
    batch(() => {
      dispatch(setSelectedPackage(cardData));
      dispatch(
        fetchSubscriptionPackage(
          currentUser.accountCode,
          1,
          addOnId || null,
        ),
      );
      dispatch(
        title === 'Body Heatmap' || title === 'Habit Tracker' || title === 'VBT Dashboard'
          ? setIsUpdatedUpgradeModalShowing(true)
          : setIsUpgradeModalShowing(true),
      );
    });
  };

  const handlePurchaseButtonClick = (e) => {
    // Prevent CardWrapper click event from firing
    e.stopPropagation();
    subscriptionFlowTracker('Card Purchase Button Clicked', legacyTitle || title, 'AMS Homepage');
    batch(() => {
      dispatch(setSelectedPackage(cardData));
      dispatch(
        fetchSubscriptionPackage(
          currentUser.accountCode,
          1,
          addOnId || null,
        ),
      );
      dispatch(setIsInstantPaymentModalShowing(true));
    });
  };

  return (
    <CardWrapper
      bgImage={backgroundImage}
      hasButton={purchaseButton}
      gradient={gradient}
      onClick={() => {
        if (locked) {
          handleLockedCardClick();
        } else {
          handleUnlockedCardClick();
        }
      }}
    >
      <CardImage bgImage={backgroundImage} />
      <InnerContent hasButton={purchaseButton}>
        <CardText>
          <CardHeaderContainer>
            <CardHeader>
              <ModuleIcon>
                <IcomoonReact
                  iconSet={iconSet}
                  color='#444444'
                  size={20}
                  icon={icon}
                />
              </ModuleIcon>
              <ModuleTitle>{title}</ModuleTitle>
            </CardHeader>
            {legacyTitle && (
              <ModuleSubtitle>
                (Formerly
                {' '}
                {legacyTitle}
                )
              </ModuleSubtitle>
            )}
          </CardHeaderContainer>
          <CardDescription>{description}</CardDescription>
        </CardText>
        {purchaseButton && (
          <PurchaseButton
            onClick={handlePurchaseButtonClick}
          >
            PURCHASE ADD ON
          </PurchaseButton>
        )}
      </InnerContent>
      {locked && !purchaseButton && (
        <>
          <LockOverlay />
          <LockIconCircle backgroundColor={gradient}>
            <IcomoonReact
              iconSet={iconSet}
              color='#FFFFFF'
              size={32}
              icon='lock'
            />
          </LockIconCircle>
        </>
      )}
    </CardWrapper>
  );
};

ModuleCard.propTypes = {
  cardData: PropTypes.instanceOf(Object).isRequired,
  purchaseButton: PropTypes.bool,
  gradient: PropTypes.string,
  locked: PropTypes.bool.isRequired,
};

ModuleCard.defaultProps = {
  purchaseButton: false,
  gradient: '',
};

export default ModuleCard;
